import { IAssemblyData } from '../data/assembly/assembly';
import editorEvent, { EditorEventName } from '../event/editorEvent';
import { modelManager } from '../manager/entityManager';
import { IPoint2d } from '../math/IPoint2d';
import { Line2d } from '../math/Line2d';
import { LineSeg2d } from '../math/LineSeg2d';
import { Point2dUtils } from '../math/Point2dUtils';
import { getRotatedRectangleVertices } from '../math/utils';
import { TGlobalStores } from '../stores';
import { EEntityType, EOperateMode, EToolType, ISelected } from '../types';
import { EOperationType } from '../types/operation';

type BoxData = { center: IPoint2d; width: number; thickness: number; rotation: number };

export enum EAlignment {
  left = 'left',
  right = 'right',
  front = 'front',
  back = 'back',
  horizontal = 'horizontal',
  vertical = 'vertical'
}

export class AlignOperation {
  private _globalStore: TGlobalStores;
  private _editorStateStore: TGlobalStores['editorStateStore'];
  private _history: TGlobalStores['history'];
  private _modelStore: TGlobalStores['modelStore'];
  private _assemblyStore: TGlobalStores['assemblyStore'];

  constructor(gs: TGlobalStores) {
    this._globalStore = gs;
    this._editorStateStore = gs.editorStateStore;
    this._history = gs.history;
    this._modelStore = gs.modelStore;
    this._assemblyStore = gs.assemblyStore;
  }

  private _getEntityData(entity: ISelected) {
    const data =
      entity.type === EEntityType.Assembly
        ? this._assemblyStore.getAssemblyData(entity.entityId)
        : this._modelStore.getFurnitureData(entity.entityId);

    return data;
  }

  /** 获取当前对齐模式下的计算点 */
  private _getAlignTargetPoint(
    center: IPoint2d,
    rotationRad: number,
    points: IPoint2d[],
    alignment: EAlignment
  ): IPoint2d {
    const cos = Math.cos(-rotationRad);
    const sin = Math.sin(-rotationRad);
    const localPoints = points.map((v) => {
      const dx = v.x - center.x;
      const dy = v.y - center.y;

      const localX = dx * cos - dy * sin;
      const localY = dx * sin + dy * cos;

      return { x: localX, y: localY };
    });

    function isNextPoint(prev: IPoint2d, next: IPoint2d) {
      switch (alignment) {
        case EAlignment.left:
          return next.x < prev.x;
        case EAlignment.right:
          return next.x > prev.x;
        case EAlignment.front:
          return next.y < prev.y;
        case EAlignment.back:
          return next.y > prev.y;
        default:
          return false;
      }
    }

    const targetPointIdx = localPoints.reduce(
      (acc, current, idx, currLocalPoints) =>
        idx === 0 ? acc : isNextPoint(currLocalPoints[acc], current) ? idx : acc,
      0
    );

    return points[targetPointIdx];
  }

  /** 获取对齐参考线的两点 */
  private _getReferenceLinePoints(referenceEntityData: BoxData, alignment: EAlignment): [IPoint2d, IPoint2d] {
    const { center, width, thickness, rotation } = referenceEntityData;
    const referenceBoundsBox = getRotatedRectangleVertices(width, thickness, center, rotation);
    const [leftBottom, rightBottom, rightTop, leftTop] = referenceBoundsBox;

    switch (alignment) {
      case EAlignment.right:
        return [rightBottom, rightTop];
      case EAlignment.front:
        return [leftBottom, rightBottom];
      case EAlignment.back:
        return [rightTop, leftTop];
      case EAlignment.horizontal:
        return [center, new LineSeg2d(rightTop, leftTop).getFootOfPerpendicular(center)];
      case EAlignment.vertical:
        return [new LineSeg2d(rightBottom, rightTop).getFootOfPerpendicular(center), center];
      case EAlignment.left:
      default:
        return [leftTop, leftBottom];
    }
  }

  /** 获取对齐后的家具/家具组信息  */
  private _getAlignedEntity(entity: ISelected, referenceBounds: BoxData, referenceLine: Line2d, alignment: EAlignment) {
    const entityData = this._getEntityData(entity);
    if (!entityData) return undefined;

    const { center, width, thickness, rotation } = entityData;

    const boundsBox = getRotatedRectangleVertices(width, thickness, center, rotation);
    const targetPoints =
      alignment === EAlignment.horizontal || alignment === EAlignment.vertical
        ? center
        : this._getAlignTargetPoint(referenceBounds.center, referenceBounds.rotation, boundsBox, alignment);

    const foot = referenceLine.pointProjectOnLine(targetPoints);
    const offset = Point2dUtils.subtracted(foot, targetPoints);

    return {
      ...entityData,
      prevCenter: Point2dUtils.clone(entityData.center),
      center: Point2dUtils.added(entityData.center, offset)
    };
  }

  /** 移动家具组 */
  private _moveAssembly(assemblyId: string, newAssemblyData: IAssemblyData, prevCenter: IPoint2d) {
    this._assemblyStore.updateAssemblyData(assemblyId, newAssemblyData);
    const offset = Point2dUtils.subtracted(newAssemblyData.center, prevCenter);

    for (const furnitureId of newAssemblyData.accessorieIds) {
      const furnitureData = this._modelStore.getFurnitureData(furnitureId);
      if (!furnitureData) continue;

      const nextCenter = Point2dUtils.added(furnitureData.center, offset);
      this._modelStore.updateFurnitureData(furnitureId, { center: nextCenter });
    }
  }

  private _historyAlignEntity(
    pendingEntities: ISelected[],
    referenceEntityData: BoxData,
    referenceLine: Line2d,
    alignment: EAlignment
  ) {
    const assemblyIds: string[] = [];
    let furnitureIds: string[] = [];
    this._history.startOperation();
    for (const entity of pendingEntities) {
      const { entityId: id } = entity;
      const entityData = this._getAlignedEntity(entity, referenceEntityData, referenceLine, alignment);
      if (entityData) {
        const { prevCenter, ...newEntityData } = entityData;
        if ('accessorieIds' in newEntityData) {
          assemblyIds.push(id);
          furnitureIds = furnitureIds.concat(newEntityData.accessorieIds);
          this._moveAssembly(id, newEntityData, prevCenter);
        } else {
          furnitureIds.push(id);
          this._modelStore.updateFurnitureData(id, newEntityData);
        }
      }
    }

    this._history.completeOperation(EOperationType.Align);

    return [assemblyIds, furnitureIds];
  }

  toggleAlignModeEnabled(enable: boolean, withoutUpdateOperateInfo?: boolean) {
    this._editorStateStore.setAlignModeEnabled(enable);
    if (!withoutUpdateOperateInfo) {
      this._globalStore.operateInfo = enable
        ? { mode: EOperateMode.Alignment, toolType: EToolType.empty }
        : { mode: EOperateMode.EMPTY, toolType: EToolType.empty };
    }
    if (!enable) {
      modelManager.alignManager.updateAlignReference();
    }
  }

  setReferenceLineVisible(visible: boolean) {
    modelManager.alignManager.setAlignLineVisible(visible);
  }

  showReferenceLine(alignment: EAlignment) {
    const { reference, multiBox } = this._editorStateStore.alignData;
    if (reference?.entityId) {
      const referenceEntityData = this._getEntityData(reference);
      if (!referenceEntityData) return;
      const [start, end] = this._getReferenceLinePoints(referenceEntityData, alignment);
      modelManager.alignManager.updateAlignLine(start, end);
      this.setReferenceLineVisible(true);
    } else if (multiBox) {
      const [start, end] = this._getReferenceLinePoints(multiBox, alignment);
      modelManager.alignManager.updateAlignLine(start, end);
      this.setReferenceLineVisible(true);
    }
  }

  toggleFurnitureAlignment(alignment: EAlignment) {
    const { reference, multiBox, pendingEntities } = this._editorStateStore.alignData;
    const referenceEntityData = reference ? this._getEntityData(reference) : multiBox;
    if (!referenceEntityData) return;

    const [start, end] = this._getReferenceLinePoints(referenceEntityData, alignment);
    const referenceLine = new Line2d({ points: { p1: start, p2: end } });

    const [assemblyIds, furnitureIds] = this._historyAlignEntity(
      pendingEntities,
      referenceEntityData,
      referenceLine,
      alignment
    );
    this._history.mergeLastTwoOperationByType(EOperationType.Align);

    for (const assemblyId of assemblyIds) {
      modelManager.assemblyManager.updateAssembly({ id: assemblyId });
    }
    for (const furnitureId of furnitureIds) {
      modelManager.furnitureManager.updateEntity({ id: furnitureId });
    }

    modelManager.alignManager.updateAlignReference();
    this._editorStateStore.refreshFurnitureMultiSelected();
    modelManager.selectedRerender();
    editorEvent.emit(EditorEventName.REFRESH_SELECTED);
  }
}
